import { HStack, Stack } from '@chakra-ui/react';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { StyledScrollWrapper } from '$/components/common/StyledScrollWrapper';
import { CollectionFilter } from '$/components/core/Collection/CollectionFilter';
import { MaterialDetailsModal } from '$/components/core/Collection/MaterialDetailsModal';
import { useMaterialDetailStore } from '$/components/core/Collection/MaterialDetailsModal/store/useMaterialDetailStore';
import { DataSortingRow } from '$/components/core/Collection/MaterialPresentation/DataSortingRow';
import { MaterialRow } from '$/components/core/Collection/MaterialRow';
import { SearchInput } from '$/components/core/Collection/MaterialSearch/SearchInput';
import { useIsMobile } from '$/hooks/useIsMobile';
import { BackToTopButton } from '$/pages/DashboardPages/components/BackToTopButton';
import { DashboardSectionRow } from '$/pages/DashboardPages/components/DashboardSectionRow';
import { CollectionHeader } from '$/pages/DashboardPages/pages/Collection/components/CollectionHeader';
import { FavoriteModal } from '$/pages/DashboardPages/pages/Favorite/components/FavoriteModal';
import { type MaterialType } from '$/services/usecases/materials/mapper/materials';

export const CollectionDashboard = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const sectionRef = useRef<HTMLDivElement>(null);

  const setActiveMaterial = useMaterialDetailStore.useSetActive();

  const [materialCount, setMaterialCount] = useState<
    Record<MaterialType, number | null>
  >({
    color_tint: null,
    wall_texture: null,
    floor_texture: null,
    facade_texture: null,
  });

  const onMaterialResult = useCallback<
    (count: number, type: MaterialType) => void
  >(
    (count, type) => {
      setMaterialCount((prev) => ({ ...prev, [type]: count }));
    },
    [setMaterialCount],
  );

  const hasSingleMaterialType = (
    materials: Record<MaterialType, number | null>,
  ): MaterialType | null => {
    let foundMaterialType: MaterialType | null = null;
    let hasNull = false;

    for (const [type, count] of Object.entries(materials)) {
      if (count === null) {
        hasNull = true;
        return null;
      }
      if (count > 0 && foundMaterialType) return null;
      if (count > 0) foundMaterialType = type as MaterialType;
    }

    if (hasNull) return null;

    return foundMaterialType;
  };

  const navigate = useNavigate();

  useEffect(() => {
    const singleMaterial = hasSingleMaterialType(materialCount);

    switch (singleMaterial) {
      case 'color_tint':
        void navigate({ to: '/collections/colors' });
        return;
      case 'wall_texture':
        void navigate({ to: '/collections/walls' });
        return;
      case 'floor_texture':
        void navigate({ to: '/collections/floors' });
        return;
      case 'facade_texture':
        void navigate({ to: '/collections/facades' });
        return;
    }
  }, [materialCount, navigate]);

  return (
    <>
      <Stack
        ref={sectionRef}
        w='full'
        {...(!isMobile && { layerStyle: 'DashboardSection' })}
      >
        <StyledScrollWrapper>
          <Stack
            gap='0'
            overflowY='auto'
            w='full'
            {...(!isMobile && {
              p: '7',
            })}
          >
            <HStack
              align='flex-start'
              justify='space-between'
              flexWrap={{ base: 'wrap', mobile: 'nowrap' }}
              gap={{ base: '3', mobile: '4' }}
              mb='2'
            >
              <CollectionHeader />

              <Stack w='100%' maxW={{ base: '100%', sm: '275px' }}>
                <SearchInput
                  h='48px'
                  searchLabel={t('dashboard.collection.searchColors')}
                />
              </Stack>
            </HStack>

            <CollectionFilter
              sectionRef={sectionRef}
              amount={
                Object.values(materialCount).reduce(
                  (sum, value) => (sum ?? 0) + (value ?? 0),
                  0,
                ) ?? 0
              }
            />

            <DataSortingRow
              isLoading={Object.values(materialCount).every((count) => !count)}
              hideCount
              scrollRefs={[sectionRef]}
            />

            <Stack gap='6' pt='6'>
              <MaterialRow
                type='color_tint'
                showAllUrl='/collections/colors'
                headline={t('dashboard.collection.search.colorTones')}
                onResult={onMaterialResult}
                wrapperRef={sectionRef}
                RowWrapper={DashboardSectionRow}
                onSelect={setActiveMaterial}
              />
              <MaterialRow
                type='facade_texture'
                showAllUrl='/collections/facades'
                headline={t('dashboard.collection.facades')}
                onResult={onMaterialResult}
                wrapperRef={sectionRef}
                RowWrapper={DashboardSectionRow}
                onSelect={setActiveMaterial}
              />
              <MaterialRow
                type='wall_texture'
                showAllUrl='/collections/walls'
                headline={t('dashboard.collection.search.wallCoverings')}
                onResult={onMaterialResult}
                wrapperRef={sectionRef}
                RowWrapper={DashboardSectionRow}
                onSelect={setActiveMaterial}
              />
              <MaterialRow
                type='floor_texture'
                showAllUrl='/collections/floors'
                headline={t('dashboard.collection.search.floorCoverings')}
                onResult={onMaterialResult}
                wrapperRef={sectionRef}
                RowWrapper={DashboardSectionRow}
                onSelect={setActiveMaterial}
              />
            </Stack>

            <BackToTopButton
              containerRef={sectionRef}
              right='65px'
              bottom='65px'
            />
          </Stack>
        </StyledScrollWrapper>
      </Stack>

      <MaterialDetailsModal />
      <FavoriteModal />
    </>
  );
};
